import {
  MDSButtonSize,
  MDSButtonSlotName,
  MDSButtonVariant,
} from "@/mds/components/buttons/button/button.types";
import { MDSText, MDSTextColor } from "@/mds/components/text";
import styled from "@emotion/styled";
import {
  MDSButton,
  MDSIcon,
  MDSTextVariant,
  MDSTextWeight,
  MDSIconKind,
  MDSMenuContentItemKind,
  MDSMenuContentItem,
  MDSMenuContentList,
  MDSMenuContentItemDivider,
} from "@/mds";
import { MemCommonEditorActionKind } from "@mem-labs/common-editor";
import { UNTITLED_TEMPLATE_TITLE } from "@/domains/untitled/untitled";
import { uuidModule } from "@/modules/uuid";
import { MemCommonEditorStore } from "@/components/note/store/mem-common-editor-store";
import { observer } from "mobx-react-lite";
import { useAppStore } from "@/store/utils/hooks";
import { SettingsTabId } from "@/store/pages/SettingsPageStore/types";
import { EventContext, TrackedEvent, TrackedEventContext, trackEvent } from "@/domains/metrics";
import { HoverActions } from "@/components/note/editor/hover-actions";
import { NoteType } from "@/components/note/types";
import { MDSFadeHorizontalScroll } from "@/mds";
import { getCalendarEventsMenuContentList } from "@/domains/calendar/getCalendarEventsMenuContentList";

export const EmptyNoteActions = observer(
  ({ editorStore }: { editorStore: MemCommonEditorStore }) => {
    const { store } = useAppStore();

    const noteId = editorStore.id;
    const canRecordVoice = editorStore.noteType === NoteType.Note;

    const templatesContentList: MDSMenuContentList = [
      ...store.templates.sortedRecentTemplatesInteractedWithByMe.map(
        ({ template }): MDSMenuContentItem => ({
          id: template.id,
          kind: MDSMenuContentItemKind.Item,
          label: template.title || UNTITLED_TEMPLATE_TITLE,
          icon: MDSIconKind.Template,
          onClick: async () => {
            const templateContents = await template.getTemplateContentsForInsertionAsync();
            if (!templateContents) return;

            const { remoteContent, documentUpdates } = templateContents;

            editorStore.dispatchAction({
              kind: MemCommonEditorActionKind.InsertTemplate,
              payload: {
                templateId: template.id,
                templateRemoteContent: remoteContent,
                templateDocumentUpdates: documentUpdates,
                templateVariables: Object.entries(store.templates.getVariables()).map(
                  ([code, variable]) => ({
                    code,
                    value: variable.value,
                  })
                ),
              },
            });
            trackEvent(TrackedEvent.TemplateUsed, {
              template_name: template.title || UNTITLED_TEMPLATE_TITLE,
              context: TrackedEventContext.FloatingBar,
            });
          },
        })
      ),
      ...(store.templates.sortedRecentTemplatesInteractedWithByMe.length > 2
        ? [
            {
              id: "divider",
              kind: MDSMenuContentItemKind.Divider,
            } as MDSMenuContentItemDivider,
          ]
        : []),
      {
        id: "manage-templates",
        kind: MDSMenuContentItemKind.Item,
        label: "Manage templates",
        icon: MDSIconKind.GearCode,
        onClick: () => {
          store.settingsModal.open(SettingsTabId.TemplatesPage);
        },
      },
    ];

    const handleNavigateToNote = ({
      noteId,
      openInSameTab,
    }: {
      noteId: string;
      openInSameTab: boolean;
    }) => {
      store.navigation.goToNote({ noteId, openInSameTab });
      if (openInSameTab) editorStore.setReloadRequired(true);
    };

    const calendarEventsContentList: MDSMenuContentList = getCalendarEventsMenuContentList({
      activeNoteId: noteId,
      calendarEvents: store.spaceAccountCalendarEvents.ongoingAndUpcomingEvents,
      createNoteUsingCalendarEventId: ({ noteId, calendarEventId }) =>
        store.notes.createNoteUsingCalendarEventId({ noteId, calendarEventId }),
      navigateToNote: handleNavigateToNote,
      store,
      context: EventContext.EditorActionsCalendarEventDropdown,
    });
    const nextCalendarEvent = store.spaceAccountCalendarEvents.ongoingAndUpcomingEvents[0];
    const nextCalendarEventLabel = nextCalendarEvent ? (
      <MDSText color={MDSTextColor.Secondary}>
        <MDSText weight={MDSTextWeight.Smedium}>{nextCalendarEvent.primaryLabel}</MDSText>
        <MDSText weight={MDSTextWeight.Regular}> • {nextCalendarEvent.startTimeLabel}</MDSText>
      </MDSText>
    ) : (
      "No upcoming events"
    );

    return (
      <StartWithContainer>
        <StartWithLabel variant={MDSTextVariant.TextXs} weight={MDSTextWeight.SemiBold}>
          Start with
        </StartWithLabel>
        <StartWithOptionsContainer>
          {canRecordVoice && (
            <MDSButton
              variant={MDSButtonVariant.Subtle}
              size={MDSButtonSize.Small}
              onClick={() => {
                if (noteId) {
                  store.audio.createAndStartAudioRecording({ associatedNoteId: noteId });
                }
              }}
            >
              <MDSIcon slot={MDSButtonSlotName.LeftIcon} kind={MDSIconKind.Microphone} />
              Voice
            </MDSButton>
          )}
          {editorStore.noteType === NoteType.Note && !store.templates.ids.size && (
            <MDSButton
              variant={MDSButtonVariant.Subtle}
              size={MDSButtonSize.Small}
              onClick={async () => {
                const templateId = uuidModule.generate();
                await store.templates.createTemplate({ templateId });
                store.navigation.goToTemplate({ templateId });
              }}
            >
              <MDSIcon slot={MDSButtonSlotName.LeftIcon} kind={MDSIconKind.Plus} />
              Create template
            </MDSButton>
          )}
          {editorStore.noteType === NoteType.Note &&
            store.spaceAccountCalendarEvents.ongoingAndUpcomingEvents.length > 0 && (
              <HoverActions
                label={nextCalendarEventLabel}
                iconKind={MDSIconKind.Calendar}
                contentList={calendarEventsContentList}
              />
            )}
          {editorStore.noteType === NoteType.Note && store.templates.ids.size > 0 && (
            <HoverActions
              label="Templates"
              iconKind={MDSIconKind.AlignLeft}
              contentList={templatesContentList}
            />
          )}
          <MDSButton
            variant={MDSButtonVariant.Subtle}
            size={MDSButtonSize.Small}
            onClick={() =>
              editorStore.dispatchAction({
                kind: MemCommonEditorActionKind.ToggleChecklistTextFormat,
                payload: null,
              })
            }
          >
            <MDSIcon slot={MDSButtonSlotName.LeftIcon} kind={MDSIconKind.ListCheck} />
            Checklist
          </MDSButton>
          <MDSButton
            variant={MDSButtonVariant.Subtle}
            size={MDSButtonSize.Small}
            onClick={() =>
              editorStore.dispatchAction({
                kind: MemCommonEditorActionKind.InsertTable,
                payload: { columns: 2, rows: 2 },
              })
            }
          >
            <MDSIcon slot={MDSButtonSlotName.LeftIcon} kind={MDSIconKind.Table} />
            Table
          </MDSButton>
        </StartWithOptionsContainer>
      </StartWithContainer>
    );
  }
);

const StartWithContainer = styled(MDSFadeHorizontalScroll)`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin: ${({ theme }) =>
    `${theme.spacing["3"]} ${theme.spacing["3"]} ${theme.spacing["3"]} ${theme.spacing["7"]}`};
  gap: ${({ theme }) => theme.spacing["4"]};
`;

const StartWithLabel = styled(MDSText)`
  color: ${({ theme }) => theme.colors.text.tertiary};
  flex-shrink: 0;
`;

const StartWithOptionsContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${({ theme }) => theme.spacing["1"]};
`;
